<template>
  <v-form ref="formRef" autocomplete="off">
    <v-container fluid class="presupuesto-pedido-form-container pa-2">
      <div class="order-header-panel mb-3">
        <v-row dense align="center">
          <v-col cols="12" sm="5">
            <v-autocomplete
              v-model="form.proveedorId"
              :items="proveedores"
              item-text="nombre"
              item-value="id"
              outlined
              dense
              :rules="proveedorRules"
              :loading="loadingProveedores"
              placeholder="Seleccione un proveedor"
              label="Proveedor"
              color="primary"
              clearable
              hide-details="auto"
              prepend-inner-icon="mdi-domain"
            />
          </v-col>
          <v-col cols="6" sm="3">
            <v-text-field
              v-model.number="descuento"
              type="number"
              outlined
              dense
              hide-details
              min="0"
              max="100"
              step="0.01"
              label="Descuento (%)"
              :rules="descuentoRules"
              prepend-inner-icon="mdi-tag-outline"
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="recargoSeleccionado"
              :items="recargoOpciones"
              item-text="label"
              item-value="value"
              outlined
              dense
              hide-details
              clearable
              label="Recargo"
              placeholder="Sin recargo"
              prepend-inner-icon="mdi-plus-circle-outline"
              @change="onRecargoSeleccionadoChange"
            />
            <v-text-field
              v-if="recargoSeleccionado === 'manual'"
              v-model.number="recargoManual"
              type="number"
              outlined
              dense
              hide-details
              min="0"
              max="100"
              step="0.01"
              placeholder="% manual"
              class="mt-1"
              :rules="recargoRules"
            />
          </v-col>
        </v-row>
      </div>
      <v-row no-gutters>
        <movimiento-compra-list
          :items="itemsCarritoParaTabla"
          v-model="form"
          ref="compraListRef"
          :auto-open-search-dialog="false"
          :limit-cantidad-by-stock="false"
          :allow-add-zero-stock="true"
          :hide-stock-column="true"
          :hide-oferta-column="true"
          :precio-editable="true"
          :cart-table-height="'calc(100vh - 290px)'"
          @addProductToCart="addProductToCart"
          @updateCantidad="onUpdateCantidad"
          @updatePrecio="onUpdatePrecio"
          @resetForm="resetForm"
          @delete="deleteItem"
          @closeDialog="tableDialogClosed"
          @error="$emit('error', $event)"
        />
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import MovimientoCompraList from "../../movimiento/pages/MovimientoManagementPage/MovimientoList/MovimientoCompraList.vue";
import ProveedorProvider from "../../proveedor/providers/ProveedorProvider";
import RecargoProvider from "../../recargo/providers/RecargoProvider";

export default {
  name: "OrdenCompraForm",
  components: { MovimientoCompraList },
  props: {
    initialItems: { type: Array, default: () => [] },
    initialProveedorId: { type: String, default: null },
    depositoId: { type: String, default: null },
    initialDescuento: { type: Number, default: 0 },
    initialRecargo: { type: Number, default: 0 },
  },
  data() {
    return {
      form: {
        idProducto: null,
        proveedorId: null,
      },
      itemsCarrito: [],
      loadingProveedores: false,
      proveedores: [],
      descuento: 0,
      recargoSeleccionado: null,
      recargoManual: 0,
      recargosLista: [],
      descuentoRules: [
        (v) =>
          (parseFloat(v) >= 0 && parseFloat(v) <= 100) ||
          "El descuento debe ser entre 0% y 100%",
      ],
      recargoRules: [
        (v) =>
          (parseFloat(v) >= 0 && parseFloat(v) <= 100) ||
          "El recargo debe ser entre 0% y 100%",
      ],
      proveedorRules: [(v) => !!v || "El proveedor es requerido"],
    };
  },
  computed: {
    recargoOpciones() {
      const manual = { label: "Valor manual", value: "manual" };
      const deLista = (this.recargosLista || []).map((r) => ({
        label: `${r.nombre} (${r.recargo}%)`,
        value: r.id,
      }));
      return [manual, ...deLista];
    },
    recargoNum() {
      if (this.recargoSeleccionado === "manual") {
        const v = this.recargoManual;
        return v === "" || v == null ? 0 : parseFloat(v);
      }
      if (this.recargoSeleccionado) {
        const r = (this.recargosLista || []).find((x) => x.id === this.recargoSeleccionado);
        return r ? r.recargo || 0 : 0;
      }
      return 0;
    },
    subtotal() {
      let total = 0;
      for (const item of this.itemsCarrito || []) {
        const p = item.producto || item.product;
        const precio = p && p.precioCompra != null ? Number(p.precioCompra) : 0;
        const cantidad = Math.max(0, Number(item.cantidad) || 0);
        total += precio * cantidad;
      }
      return total;
    },
    itemsCarritoParaTabla() {
      return (this.itemsCarrito || []).map((item) => {
        const p = item.producto || item.product;
        const precioBase = p && p.precioCompra != null ? Number(p.precioCompra) : 0;
        const cantidad = Number(item.cantidad) || 0;
        const marca = p && p.marca && p.marca.nombre ? p.marca.nombre : p && p.marca ? p.marca : "";
        const modelo = p && p.modelo && p.modelo.nombre ? p.modelo.nombre : p && p.modelo ? p.modelo : "";
        const tamanio = p && p.tamanio && p.tamanio.tamanio ? p.tamanio.tamanio : p && p.tamanio ? p.tamanio : null;
        const nombre = (p && p.nombre) || [marca, modelo, tamanio].filter(Boolean).join(" · ") || "-";
        return {
          id: (p && (p.id || p._id)) || null,
          stock: (p && p.stock) != null ? p.stock : 0,
          nombre,
          precio: precioBase,
          precioBase,
          cantidad,
          ofertaAplicada: null,
          precioManual: !!item.precioManual,
        };
      });
    },
    precioTotal() {
      const desc = Number(this.descuento) || 0;
      const rec = this.recargoNum;
      const total = this.subtotal * (1 - desc / 100) * (1 + rec / 100);
      return total.toFixed(2);
    },
  },
  watch: {
    initialItems: {
      immediate: true,
      handler(items) {
        if (items && items.length > 0) {
          this.itemsCarrito = items.map((b) => ({
            producto: b.product || b.producto,
            cantidad: Math.max(1, Number(b.cantidad) || 1),
            precioManual: !!b.precioManual,
            precioCatalogo: b.precioCatalogo != null ? Number(b.precioCatalogo) : null,
          }));
        }
      },
    },
    initialProveedorId: {
      immediate: true,
      handler(id) {
        if (id) this.form.proveedorId = id;
      },
    },
    initialDescuento: {
      immediate: true,
      handler(val) {
        this.descuento = Number(val) || 0;
      },
    },
    initialRecargo: {
      immediate: true,
      handler(val) {
        const v = Number(val) || 0;
        if (v > 0) {
          this.recargoSeleccionado = "manual";
          this.recargoManual = v;
        } else {
          this.recargoSeleccionado = null;
          this.recargoManual = 0;
        }
      },
    },
    precioTotal: {
      immediate: true,
      handler(v) {
        this.$emit("total-change", {
          total: v,
          subtotal: this.subtotal.toFixed(2),
          descuento: Number(this.descuento) || 0,
          recargo: this.recargoNum,
        });
      },
    },
  },
  created() {
    if (this.initialProveedorId) this.form.proveedorId = this.initialProveedorId;
    if (this.initialItems && this.initialItems.length > 0) {
      this.itemsCarrito = this.initialItems.map((b) => ({
        producto: b.product || b.producto,
        cantidad: Math.max(1, Number(b.cantidad) || 1),
      }));
    }
    this.loadProveedores();
    this.loadRecargos();
  },
  methods: {
    onRecargoSeleccionadoChange() {
      if (this.recargoSeleccionado !== "manual") {
        const r = (this.recargosLista || []).find((x) => x.id === this.recargoSeleccionado);
        this.recargoManual = r ? r.recargo || 0 : 0;
      }
    },
    async loadRecargos() {
      try {
        const r = await RecargoProvider.fetchRecargos();
        const list = (r.data && r.data.recargoFetch) || [];
        this.recargosLista = Array.isArray(list) ? list : [];
      } catch (e) {
        this.recargosLista = [];
      }
    },
    async loadProveedores() {
      this.loadingProveedores = true;
      try {
        const r = await ProveedorProvider.fetchProveedores();
        this.proveedores = (r.data && r.data.proveedorFetch) || [];
      } finally {
        this.loadingProveedores = false;
      }
    },
    onUpdateCantidad({ item, newCantidad }) {
      const productId = item.id || (item.producto && (item.producto.id || item.producto._id));
      const row = this.itemsCarrito.find(
        (it) =>
          String((it.producto || it.product) && ((it.producto || it.product).id || (it.producto || it.product)._id)) ===
          String(productId)
      );
      if (row) {
        row.cantidad = Math.max(1, Number(newCantidad) || 1);
      }
    },
    onUpdatePrecio({ item, newPrecio }) {
      const productId = item.id || (item.producto && (item.producto.id || item.producto._id));
      const row = this.itemsCarrito.find(
        (it) =>
          String((it.producto || it.product) && ((it.producto || it.product).id || (it.producto || it.product)._id)) ===
          String(productId)
      );
      if (!row) return;
      const p = row.producto || row.product;
      if (!p) return;
      const precio = Math.round((Number(newPrecio) || 0) * 100) / 100;
      if (row.precioCatalogo == null && p.precioCompra != null && Number.isFinite(Number(p.precioCompra))) {
        this.$set(row, "precioCatalogo", Math.round(Number(p.precioCompra) * 100) / 100);
      }
      this.$set(p, "precioCompra", precio);
      const catalogo = row.precioCatalogo != null ? Number(row.precioCatalogo) : precio;
      this.$set(row, "precioManual", Math.abs(precio - catalogo) > 0.005);
    },
    addProductToCart(item) {
      const existing = this.itemsCarrito.find(
        (it) => (it.producto && it.producto.id) === (item.producto && item.producto.id)
      );
      const cantidadNueva = item.cantidad || 1;
      if (existing) {
        existing.cantidad += cantidadNueva;
      } else {
        const p = item.producto;
        const catalogo = p && p.precioCompra != null ? Number(p.precioCompra) : 0;
        this.itemsCarrito.push({
          producto: p,
          cantidad: cantidadNueva,
          precioManual: false,
          precioCatalogo: catalogo,
        });
      }
    },
    deleteItem(item) {
      const index = this.itemsCarrito.findIndex(
        (it) => (it.producto && (it.producto.id || it.producto._id)) === item.id
      );
      if (index > -1) this.itemsCarrito.splice(index, 1);
    },
    resetForm() {},
    tableDialogClosed() {},
    getPayload() {
      const itemsWithQty = (this.itemsCarrito || []).filter((row) => Number(row.cantidad) >= 1);
      return {
        proveedorId: this.form.proveedorId,
        descuento: Number(this.descuento) || 0,
        recargo: this.recargoNum,
        items: itemsWithQty.map((row) => {
          const p = row.producto || row.product;
          const cantidad = Number(row.cantidad) || 1;
          const precioUnitario = p && p.precioCompra != null ? Number(p.precioCompra) : 0;
          const catalogo = row.precioCatalogo != null ? Number(row.precioCatalogo) : null;
          const precioManual =
            !!row.precioManual ||
            (catalogo != null && Number.isFinite(catalogo) && Math.abs(precioUnitario - catalogo) > 0.005);
          return {
            product: p,
            producto: p,
            cantidad,
            precioUnitario,
            precioManual,
          };
        }),
      };
    },
    valid() {
      if (this.$refs.formRef) {
        this.$refs.formRef.validate();
      }
      if (!this.form.proveedorId || this.itemsCarrito.length === 0) return false;
      const conCantidad = this.itemsCarrito.filter((row) => Number(row.cantidad) >= 1);
      return conCantidad.length > 0;
    },
    pedidoExcedeStock() {
      return false;
    },
  },
};
</script>

<style scoped>
.presupuesto-pedido-form-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.order-header-panel {
  flex-shrink: 0;
  background: #f4f6fb;
  border-radius: 8px;
  padding: 10px 14px;
  border-left: 4px solid #1976d2;
  box-shadow: 0 1px 3px rgba(25, 118, 210, 0.08);
}
</style>
